import React, { useEffect, useState } from 'react';
import { Loader2, Check, X, ExternalLink, BadgeCheck, MapPin, Eye, EyeOff, Clock, Globe, Phone, Mail, Calendar, Pencil, Trash2, RotateCcw } from 'lucide-react';
import api, { mediaUrl } from '../../lib/api';
import { sanitizeHtml } from '../../lib/sanitize';
import CenterForm from '../../components/CenterForm';
import { CENTER_TYPES, typeLabel, dayLabel } from '../../lib/centerOptions';
import { useToast } from '../../hooks/use-toast';

const STATUS_TABS = [['pending', 'Pending'], ['active', 'Active'], ['inactive', 'Hidden / Rejected']];

const AdminCenters = () => {
  const { toast } = useToast();
  const [status, setStatus] = useState('pending');
  const [type, setType] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [editing, setEditing] = useState(null);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = { status };
      if (type) params.type = type;
      const res = await api.get('/admin/centers', { params });
      setItems(res.data || []);
    } catch (e) { console.error('Failed to load centers:', e); } finally { setLoading(false); }
  };
  useEffect(() => { fetchItems(); /* eslint-disable-next-line */ }, [status, type]);

  const approve = async (c) => {
    try { await api.post(`/admin/centers/${c.id}/approve`); toast({ title: 'Approved ✅' }); fetchItems(); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
  };
  const reject = async (c) => {
    try { await api.post(`/admin/centers/${c.id}/reject`, { reason }); toast({ title: 'Rejected' }); setRejecting(null); setReason(''); fetchItems(); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
  };
  const hide = async (c) => {
    try { await api.post(`/admin/centers/${c.id}/hide`); toast({ title: 'Hidden from directory' }); fetchItems(); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
  };
  const restore = async (c) => {
    try { await api.post(`/admin/centers/${c.id}/unhide`); toast({ title: 'Restored & live' }); fetchItems(); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Permanently delete "${c.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/centers/${c.id}`); toast({ title: 'Deleted' }); fetchItems(); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
  };

  if (editing) {
    return (
      <div className="max-w-3xl">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-serif-display text-3xl text-burgundy">Edit Center</h1>
          <button onClick={() => setEditing(null)} className="text-mahogany/70 hover:text-mahogany"><X className="w-5 h-5" /></button>
        </div>
        <CenterForm center={editing} onDone={() => { setEditing(null); fetchItems(); }} onCancel={() => setEditing(null)} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="eyebrow mb-2">Admin</div>
        <h1 className="font-serif-display text-4xl text-burgundy">Centers <span className="font-italic-display text-rose-italic">Directory</span></h1>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-full bg-white/70 border border-rose/50 p-1" data-testid="admin-center-status-tabs">
          {STATUS_TABS.map(([v, l]) => (
            <button key={v} onClick={() => setStatus(v)} data-testid={`admin-center-tab-${v}`}
              className={`px-4 py-1.5 rounded-full text-sm ${status === v ? 'bg-mahogany text-cream' : 'text-mahogany/70 hover:bg-rose-soft/40'}`}>{l}</button>
          ))}
        </div>
        <select value={type} onChange={(e) => setType(e.target.value)} data-testid="admin-center-type-filter"
          className="bg-white/70 rounded-full border border-rose/50 px-4 py-2 text-burgundy focus:outline-none">
          <option value="">All types</option>
          {CENTER_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-mahogany"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="p-8 text-center text-mahogany/60 bg-white/60 rounded-2xl border border-rose/40" data-testid="admin-centers-empty">No centers in this queue.</div>
      ) : (
        <ul className="space-y-3" data-testid="admin-centers-list">
          {items.map((c) => (
            <li key={c.id} className="p-5 rounded-2xl border border-rose/50 bg-white/70" data-testid={`admin-center-${c.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-serif-display text-xl text-burgundy">{c.name}</span>
                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-rose-soft text-mahogany">{typeLabel(c.type)}</span>
                    {c.is_verified && <BadgeCheck className="w-4 h-4 text-green-600" />}
                    {c.neo_sannyas_based && <span className="text-[10px] px-2 py-0.5 rounded-full bg-cream-2 text-mahogany">Neo-Sannyas</span>}
                  </div>
                  {c.short_description && <p className="mt-2 text-sm text-burgundy/85 line-clamp-2">{c.short_description}</p>}
                  <div className="mt-2 flex items-center gap-2 text-sm text-mahogany/70"><MapPin className="w-4 h-4" /> {[c.location?.city, c.location?.country].filter(Boolean).join(', ') || '—'}</div>
                  <div className="mt-1 text-xs text-mahogany/60">Contact: {c.contact_person || '—'} · {c.contact_phone || '—'} · {c.contact_email || '—'}</div>
                  <div className="mt-1 text-xs text-mahogany/70 bg-cream-2/70 rounded-lg px-2 py-1 inline-block" data-testid={`submitter-${c.id}`}>
                    Submitted by: <span className="font-medium text-burgundy">{c.created_by_name || 'Unknown'}</span>
                    {c.created_by_email ? ` · ${c.created_by_email}` : ''}
                    {c.created_by_role ? ` · ${c.created_by_role}` : ''}
                  </div>
                </div>
                <div className="space-y-2">
                  <button onClick={() => setExpanded(expanded === c.id ? null : c.id)} data-testid={`details-center-${c.id}`} className="pill-btn-outline"><Eye className="w-4 h-4" /> {expanded === c.id ? 'Hide' : 'View'} details</button>
                  {c.status === 'active' && (
                    <button onClick={() => window.open(`/centers/${c.slug}`, '_blank')} className="pill-btn-outline"><ExternalLink className="w-4 h-4" /> Preview</button>
                  )}
                  {status === 'pending' && (
                    <div className="flex gap-2">
                      <button onClick={() => approve(c)} data-testid={`approve-center-${c.id}`} className="pill-btn bg-green-100 text-green-800 hover:bg-green-200"><Check className="w-4 h-4" /> Approve</button>
                      <button onClick={() => setRejecting(c.id)} data-testid={`reject-center-${c.id}`} className="pill-btn-outline text-red-700 border-red-300 hover:bg-red-50"><X className="w-4 h-4" /> Reject</button>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => setEditing(c)} data-testid={`edit-center-${c.id}`} className="pill-btn-outline"><Pencil className="w-4 h-4" /> Edit</button>
                    {c.status === 'active' && (
                      <button onClick={() => hide(c)} data-testid={`hide-center-${c.id}`} className="pill-btn-outline text-amber-700 border-amber-300 hover:bg-amber-50"><EyeOff className="w-4 h-4" /> Hide</button>
                    )}
                    {c.status === 'inactive' && (
                      <button onClick={() => restore(c)} data-testid={`restore-center-${c.id}`} className="pill-btn-outline text-green-700 border-green-300 hover:bg-green-50"><RotateCcw className="w-4 h-4" /> Restore</button>
                    )}
                    <button onClick={() => remove(c)} data-testid={`delete-center-${c.id}`} className="pill-btn-outline text-red-700 border-red-300 hover:bg-red-50"><Trash2 className="w-4 h-4" /> Delete</button>
                  </div>
                </div>
              </div>

              {c.status === 'inactive' && c.rejection_reason && (
                <div className="mt-3 p-3 bg-red-50 rounded-xl text-sm text-red-700" data-testid={`rejection-note-${c.id}`}>
                  <span className="font-medium">Feedback sent to submitter:</span> {c.rejection_reason}
                </div>
              )}

              {expanded === c.id && <DetailsPanel c={c} />}

              {rejecting === c.id && (
                <div className="mt-3 p-3 bg-red-50 rounded-xl space-y-2">
                  <label className="block text-xs uppercase tracking-wider text-red-700/80">Reason &amp; feedback (visible to submitter — they can edit &amp; resubmit)</label>
                  <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="e.g. Please add a valid address and a cover image, then resubmit." data-testid="reject-reason-input"
                    className="w-full bg-white rounded-lg px-3 py-2 border border-red-200 focus:outline-none text-sm" />
                  <div className="flex gap-2">
                    <button onClick={() => reject(c)} className="text-xs px-3 py-1.5 rounded-full bg-red-600 text-white" data-testid="confirm-reject-btn">Send &amp; Reject</button>
                    <button onClick={() => { setRejecting(null); setReason(''); }} className="text-xs px-3 py-1.5 rounded-full bg-white border border-red-200 text-red-700">Cancel</button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const DetailsPanel = ({ c }) => {
  const s = c.social || {};
  return (
    <div className="mt-3 p-4 rounded-xl bg-cream-2/60 border border-rose/40 space-y-4" data-testid={`center-details-${c.id}`}>
      {c.cover_image && <img src={mediaUrl(c.cover_image)} alt="" className="w-full max-h-56 object-cover rounded-lg" />}
      {c.description && <div className="prose-osho text-sm text-burgundy/85" dangerouslySetInnerHTML={{ __html: sanitizeHtml(c.description) }} />}

      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-burgundy/85">
        <Info icon={MapPin}>{[c.location?.address, c.location?.city, c.location?.state, c.location?.country, c.location?.pincode].filter(Boolean).join(', ') || '—'}</Info>
        {c.location?.google_maps_url && <Info icon={MapPin}><a href={c.location.google_maps_url} target="_blank" rel="noreferrer" className="text-rose-italic hover:underline">Google Maps</a></Info>}
        {c.contact_phone && <Info icon={Phone}>{c.contact_phone}</Info>}
        {c.contact_email && <Info icon={Mail}>{c.contact_email}</Info>}
        {s.website && <Info icon={Globe}><a href={s.website} target="_blank" rel="noreferrer" className="hover:underline break-all">{s.website}</a></Info>}
        {c.year_established && <Info icon={Calendar}>Established {c.year_established}</Info>}
      </div>

      {(s.facebook || s.instagram || s.youtube || s.dr_meditation) && (
        <div className="text-xs text-mahogany/70 space-x-3">
          {s.facebook && <a href={s.facebook} target="_blank" rel="noreferrer" className="hover:underline">Facebook</a>}
          {s.instagram && <a href={s.instagram} target="_blank" rel="noreferrer" className="hover:underline">Instagram</a>}
          {s.youtube && <a href={s.youtube} target="_blank" rel="noreferrer" className="hover:underline">YouTube</a>}
          {s.dr_meditation && <a href={s.dr_meditation} target="_blank" rel="noreferrer" className="hover:underline">Dr. Meditation</a>}
        </div>
      )}

      {c.programs_offered?.length > 0 && (
        <div><div className="text-xs uppercase tracking-wider text-mahogany/70 mb-1">Programs Offered</div>
          <div className="flex flex-wrap gap-1.5">{c.programs_offered.map((p) => <span key={p} className="text-xs px-2 py-0.5 rounded-full bg-rose-soft text-mahogany">{p}</span>)}</div></div>
      )}
      {c.facilities?.length > 0 && (
        <div><div className="text-xs uppercase tracking-wider text-mahogany/70 mb-1">Facilities</div>
          <div className="flex flex-wrap gap-1.5">{c.facilities.map((p) => <span key={p} className="text-xs px-2 py-0.5 rounded-full bg-white/70 border border-rose/40 text-burgundy/80">{p}</span>)}</div></div>
      )}
      {c.initiation_lineage && (
        <div><div className="text-xs uppercase tracking-wider text-mahogany/70 mb-1">Initiation Lineage</div><p className="text-sm text-burgundy/85">{c.initiation_lineage}</p></div>
      )}

      {c.hours?.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wider text-mahogany/70 mb-1 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Hours</div>
          <div className="grid sm:grid-cols-2 gap-x-6 text-sm">
            {c.hours.map((h) => (
              <div key={h.day} className="flex justify-between"><span className="text-burgundy/85">{dayLabel(h.day)}</span><span className={h.closed ? 'text-red-500' : 'text-mahogany/80'}>{h.closed ? 'Closed' : `${h.open || '—'} – ${h.close || '—'}`}</span></div>
            ))}
          </div>
        </div>
      )}

      {c.gallery?.length > 0 && (
        <div className="flex flex-wrap gap-2">{c.gallery.map((g, i) => <img key={i} src={mediaUrl(g)} alt="" className="w-16 h-16 rounded-lg object-cover" />)}</div>
      )}
    </div>
  );
};

const Info = ({ icon: Icon, children }) => (
  <div className="flex items-start gap-2"><Icon className="w-4 h-4 text-rose-italic mt-0.5 shrink-0" /> <span>{children}</span></div>
);

export default AdminCenters;
